/**
 * ``useInspectInList`` — inspect an entity from a **cross-pane link**, surface
 * it in its list, and record where the operator came from (operator request
 * 2026-06-12; REQ-UI-051 / REQ-UI-186).
 *
 * # The behaviour
 *
 * A cross-pane link — e.g. the **platform short-name** in a Detection's
 * "Detected by" section or an Alert's "Associations" / "Source" section —
 * *navigates* the operator to a different entity. Clicking it already moves the
 * **reading focus** (calls ``inspect`` → Entity Details renders the platform +
 * the Platforms list highlights + scrolls its row). But if that list panel is
 * sitting **behind another tab**, the operator can't see the synced highlight
 * without manually switching tabs. This hook closes that gap: the click also
 * **activates** the target entity's list panel.
 *
 * Since 2026-10-05 it routes through ``inspectLinked`` rather than ``inspect``,
 * so the selection it is leaving lands on the bus's navigation history and the
 * Selection Details back control can return to it (REQ-UI-186). That belongs
 * here rather than at each call site for the same reason the list reveal does:
 * this hook *is* "the operator followed a link", which is precisely the event
 * the history records.
 *
 * It is the imperative companion to :func:`useRevealMapSelectionInList` (which
 * reveals on a *map-glyph* selection): there the trigger is the ``mapSelected``
 * slot; here it's an explicit cross-pane-link click that sets ``inspected``.
 * Both surface the same list via :func:`MultiPaneApi.activatePanel`, which
 * **only reveals an already-open background tab** — a list the operator has
 * closed (or that is role-filtered out) is left closed (a no-op), and no panel
 * is ever force-opened. A list-row left-click stays a plain ``inspect`` (no
 * reveal needed — the row is already in the active list).
 *
 * Returns ``inspect`` UNCHANGED in shape — ``(value: SelectionValue) => void`` —
 * so a call site swaps ``inspect`` → ``inspectInList`` with no other change.
 *
 * Cross-references:
 *   - REQ-UI-051 (synchronized-selection bus); ADR-024 §5.
 *   - selection/selection-store.ts (``inspect`` — sets the reading focus).
 *   - multi-pane/multi-pane-context.ts (``activatePanel``).
 *   - selection/use-reveal-map-selection.ts (``LIST_PANEL_FOR_KIND``; the
 *     map-glyph reveal twin).
 */
import { useCallback } from "react";
import { useMultiPaneApi } from "@/multi-pane/multi-pane-context";
import { useSelectionActions, type EntitySelection } from "@/selection/selection-store";
import { LIST_PANEL_FOR_KIND } from "@/selection/use-reveal-map-selection";

/**
 * Return an ``inspect``-shaped callback that, in addition to setting the
 * reading focus, **reveals the entity's list panel** (activates its tab) when
 * it's present but behind another tab. Drop-in for ``inspect`` at a cross-pane
 * link.
 *
 * Entity selections only: an ADR-038 ``query-record`` originates in the Query
 * panel itself, so there is no other list to surface — those call ``inspect``
 * directly.
 */
export function useInspectInList(): (value: EntitySelection) => void {
  const { inspectLinked } = useSelectionActions();
  const multiPaneApi = useMultiPaneApi();
  return useCallback(
    (value: EntitySelection): void => {
      // Reading focus first (Entity Details + list-row highlight/scroll), and
      // the outgoing selection onto the back history (REQ-UI-186)…
      inspectLinked(value);
      // …then surface its list if it's an open background tab. ``activatePanel``
      // is a no-op when the list is closed / role-filtered / no workspace, so
      // this never force-opens a panel.
      multiPaneApi?.activatePanel(LIST_PANEL_FOR_KIND[value.kind]);
    },
    [inspectLinked, multiPaneApi],
  );
}
